import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Download, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { FaGooglePlay, FaApple } from 'react-icons/fa';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSignupDropdownOpen, setIsSignupDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/', isRoute: true },
    { name: 'Features', href: '/features', isRoute: true },
    { name: 'Sign Up', href: '#', isDropdown: true },
    { name: 'Partner', href: '/partner', isRoute: true },
    { name: 'Shop', href: '/shop', isRoute: true },
    { name: 'Support', href: '/support', isRoute: true },
    { name: 'Contact', href: '/contact', isRoute: true },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav' : 'bg-transparent'
      }`}
      data-testid="navigation"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <motion.div whileHover={{ scale: 1.02 }} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_play-connect-in/artifacts/zk1iohyx_image.png" 
                alt="Playmatez Logo" 
                className="h-10 w-auto"
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isDropdown ? (
                <div 
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setIsSignupDropdownOpen(true)}
                  onMouseLeave={() => setIsSignupDropdownOpen(false)}
                >
                  <button
                    className="flex items-center gap-1 text-muted-foreground hover:text-white transition-colors font-medium text-sm tracking-wide"
                    data-testid="nav-link-signup"
                  >
                    {link.name}
                    <ChevronDown size={16} className={`transition-transform ${isSignupDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isSignupDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-card border border-white/10 rounded-xl p-3 shadow-2xl"
                      >
                        <a
                          href="https://play.google.com/store/apps/details?id=com.app.playmatez&pcampaignid=web_share"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors"
                          data-testid="signup-google-play"
                        >
                          <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" className="h-8" />
                        </a>
                        <a
                          href="https://apps.apple.com/app/playmatez/id6450857903"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors"
                          data-testid="signup-app-store"
                        >
                          <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="App Store" className="h-8" />
                        </a>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : link.isRoute ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`transition-colors font-medium text-sm tracking-wide ${
                    isActive(link.href) ? 'text-primary' : 'text-muted-foreground hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ) : (
                <motion.a
                  key={link.name}
                  href={link.href}
                  className="text-muted-foreground hover:text-white transition-colors font-medium text-sm tracking-wide"
                  whileHover={{ y: -2 }}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </motion.a>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <motion.a
              href="https://download.playmatez.io/u/F4AE8rel"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-colors download-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              data-testid="download-app-btn"
            >
              <Download size={18} />
              Download App
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-nav border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                link.isDropdown ? (
                  <div key={link.name} className="flex flex-col gap-2">
                    <span className="text-muted-foreground font-medium py-2">Sign Up</span>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.app.playmatez&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 pl-4 py-2 text-white"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaGooglePlay className="text-[#3DDC84]" />
                      Google Play
                    </a>
                    <a
                      href="https://apps.apple.com/app/playmatez/id6450857903"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 pl-4 py-2 text-white"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaApple />
                      App Store
                    </a>
                  </div>
                ) : link.isRoute ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`transition-colors font-medium py-2 ${
                      isActive(link.href) ? 'text-primary' : 'text-muted-foreground hover:text-white'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-muted-foreground hover:text-white transition-colors font-medium py-2"
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <a
                href="https://download.playmatez.io/u/F4AE8rel"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-primary text-white font-semibold rounded-full mt-2"
                onClick={() => setIsMobileMenuOpen(false)}
                data-testid="mobile-download-btn"
              >
                <Download size={18} />
                Download App
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;
